import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import styles from "./App.module.css";
import { NotificationBell } from "./components/NotificationBell";
import { UserMenu } from "./components/UserMenu";
import { Icon, type IconName } from "./components/Icon";
import { useModuler, type ModulId } from "./lib/moduler";

type NavChild = { label: string; to?: string; disabled?: boolean };
type NavItem = {
  label: string;
  /** Kan skjules under Innstillinger → Moduler */
  modul?: ModulId;
  icon: IconName;
  to?: string;
  disabled?: boolean;
  children?: NavChild[];
};

// Hovedmenyen: grupper med underpunkter + frittstående lenker.
const NAV: NavItem[] = [
  { label: "Hjem", icon: "grid", to: "/" },
  { label: "Mine oppgaver", icon: "checkCircle", to: "/oppgaver" },
  {
    label: "Prosjekter",
    icon: "buildings",
    modul: "prosjekter",
    children: [
      { label: "Oversikt", to: "/prosjekter" },
      { label: "Nytt prosjekt", disabled: true },
    ],
  },
  {
    label: "Kalkyle",
    icon: "target",
    modul: "kalkyle",
    children: [
      { label: "Oversikt", to: "/kalkyle?fane=oversikt" },
      { label: "Ny kalkyle", to: "/kalkyle?fane=ny" },
      { label: "Rapport", to: "/kalkyle?fane=rapporter" },
    ],
  },
  {
    label: "Salg",
    icon: "trendUp",
    modul: "salg",
    children: [
      { label: "Oversikt", to: "/salg" },
      { label: "Nyeste", to: "/salg/nyeste" },
      { label: "Registrere salg", disabled: true },
    ],
  },
  { label: "Rapportering", icon: "activity", to: "/rapport" },
  { label: "Kvartalsrapport", icon: "doc", to: "/rapporter" },
  { label: "Portefølje", icon: "layers", to: "/portefolje" },
];

// Administrasjon (Brukere, Maler, Innstillinger) ligger ikke i menyen lenger (Erik 02.11.2026):
// den er samlet på Innstillinger, som åpnes fra kontomenyen nederst.

export function Sidebar({
  collapsed,
  open,
  onToggle,
}: {
  collapsed: boolean;
  /** Mobil: menyen er skjøvet inn fra venstre. */
  open: boolean;
  onToggle: () => void;
}) {
  const loc = useLocation();
  const { skjulte } = useModuler();
  const [logoFeil, setLogoFeil] = useState(false);
  // Alle grupper starter lukket (Erik 29.10.2026); et klikk åpner dem.
  const [apneGrupper, setApneGrupper] = useState<Set<string>>(new Set());

  const leafActive = (to: string): boolean => {
    const [path, search] = to.split("?");
    if (path === "/") return loc.pathname === "/";
    if (loc.pathname !== path) return false;
    if (!search) return true;
    const want = new URLSearchParams(search).get("fane");
    const have = new URLSearchParams(loc.search).get("fane");
    return want === have || (want === "oversikt" && !have);
  };

  const label = <T extends { label: string }>(item: T) => (
    <span className={styles.navLabel}>{item.label}</span>
  );

  const renderItem = (item: NavItem) => {
    // Frittstående lenke (evt. deaktivert)
    if (!item.children) {
      if (item.disabled || !item.to) {
        return (
          <div
            key={item.label}
            className={`${styles.navItem} ${styles.navItemDisabled}`}
            aria-disabled="true"
            title={collapsed ? `${item.label} (kommer snart)` : "Kommer snart"}
          >
            <Icon name={item.icon} size={16} className={styles.navIcon} />
            {label(item)}
          </div>
        );
      }
      return (
        <NavLink
          key={item.label}
          to={item.to}
          end={item.to === "/"}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
          }
        >
          <Icon name={item.icon} size={16} className={styles.navIcon} />
          {label(item)}
        </NavLink>
      );
    }

    const gruppeAktiv = item.children.some((c) => c.to && leafActive(c.to));

    // Kollapset meny: gruppen er en lenke til første underpunkt (undermenyen vises ikke).
    if (collapsed) {
      const forste = item.children.find((c) => c.to && !c.disabled);
      return (
        <Link
          key={item.label}
          to={forste?.to ?? "/"}
          title={item.label}
          className={`${styles.navItem} ${gruppeAktiv ? styles.navItemActive : ""}`}
        >
          <Icon name={item.icon} size={16} className={styles.navIcon} />
          {label(item)}
        </Link>
      );
    }

    // Gruppe med underpunkter (ACL: .nav-parent + .nav-sub). Klikk på navnet går
    // rett til første underpunkt (Oversikt) og åpner gruppen, eller lukker/åpner
    // den når man allerede er i gruppen. Pilen er en egen knapp som bare folder.
    const apen = apneGrupper.has(item.label);
    const forste = item.children.find((c) => c.to && !c.disabled);
    const settApen = (vis: boolean) =>
      setApneGrupper((s) => {
        const n = new Set(s);
        if (vis) n.add(item.label);
        else n.delete(item.label);
        return n;
      });
    return (
      <div key={item.label} className={styles.navGroup}>
        <div className={styles.navParentRow}>
          <Link
            to={forste?.to ?? "/"}
            className={`${styles.navItem} ${styles.navParent} ${
              gruppeAktiv ? styles.navParentActive : ""
            }`}
            onClick={(e) => {
              // Allerede i gruppen: klikket lukker/åpner undermenyen i stedet for å
              // laste Oversikt på nytt (som i ACL). Ellers: gå til Oversikt og åpne.
              if (gruppeAktiv) {
                e.preventDefault();
                settApen(!apen);
              } else {
                settApen(true);
              }
            }}
            aria-expanded={apen}
          >
            <Icon name={item.icon} size={16} className={styles.navIcon} />
            {label(item)}
          </Link>
          <button
            type="button"
            className={styles.navCaretBtn}
            onClick={() => settApen(!apen)}
            aria-expanded={apen}
            aria-label={`${apen ? "Skjul" : "Vis"} undermeny for ${item.label}`}
            title={apen ? "Skjul undermeny" : "Vis undermeny"}
          >
            <span className={`${styles.navCaret} ${apen ? styles.navCaretOpen : ""}`} aria-hidden="true">
              <Icon name="chevronDown" size={14} />
            </span>
          </button>
        </div>
        {apen && (
          <div className={styles.navSub} role="group" aria-label={item.label}>
            {item.children.map((c) =>
              c.disabled || !c.to ? (
                <div
                  key={c.label}
                  className={`${styles.navChild} ${styles.navChildDisabled}`}
                  aria-disabled="true"
                  title="Kommer snart"
                >
                  {c.label}
                </div>
              ) : (
                <Link
                  key={c.label}
                  to={c.to}
                  aria-current={leafActive(c.to) ? "page" : undefined}
                  className={`${styles.navChild} ${
                    leafActive(c.to) ? styles.navChildActive : ""
                  }`}
                >
                  {c.label}
                </Link>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`${styles.sidebar} ${collapsed ? styles.collapsed : ""} ${
        open ? styles.sidebarOpen : ""
      }`}
    >
      {/* Øverst: logo og knapp for å skjule menyen (panel-ikon), som i ACL */}
      <div className={styles.sideHead}>
        <NavLink to="/" className={styles.brand} title={collapsed ? "Gjelsten Bolig" : undefined}>
          {!logoFeil && !collapsed ? (
            <img
              src="/logo/gjelsten-bolig-liggende.svg"
              alt="Gjelsten Bolig"
              className={styles.brandLogo}
              onError={() => setLogoFeil(true)}
            />
          ) : (
            <span className={styles.brandMark} aria-label="GB Portal">
              GB
            </span>
          )}
        </NavLink>
        <button
          type="button"
          className={styles.sideToggle}
          onClick={onToggle}
          aria-expanded={!collapsed}
          title={collapsed ? "Vis meny" : "Skjul meny"}
          aria-label={collapsed ? "Vis meny" : "Skjul meny"}
        >
          <Icon name="panelLeft" size={20} />
        </button>
      </div>

      <nav className={styles.nav}>
        {NAV.filter((n) => !n.modul || !skjulte.includes(n.modul)).map(renderItem)}
      </nav>

      {/* Konto og varsler nederst i menyen (ingen topplinje på store skjermer) */}
      <div className={styles.sideTools}>
        <UserMenu placement="up" />
        <span className={styles.grow} />
        <NotificationBell />
      </div>
      <div className={styles.sidebarFoot}>
        <span className={styles.navLabel}>Gjelsten Bolig · levert av Coherent</span>
      </div>
    </aside>
  );
}
